// A small spring integrator, sized for the handful of places in the admin UI
// that animate something a person can grab.
//
// Springs are used rather than CSS transitions because a spring always starts
// from wherever the value currently is and keeps whatever velocity it already
// had. Re-targeting mid-flight blends into the new motion instead of cutting to
// a fresh curve, which is what makes an interaction feel interruptible.
//
// Parameters follow the two Apple exposes rather than mass/stiffness/damping:
//
//   response — roughly how long, in seconds, the value takes to reach target
//   damping  — 1.0 settles without overshoot; below 1.0 bounces
//
// Reserve bounce for motion the user threw; anything that merely appears should
// settle flat.

export function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

const MAX_STEP = 1 / 240 // s — sub-stepping keeps stiff springs stable
const REST_VALUE = 0.01
const REST_VELOCITY = 0.05

export class Spring {
  constructor({ value = 0, velocity = 0, response = 0.4, damping = 1, onUpdate, onRest } = {}) {
    this.value = value
    this.target = value
    this.velocity = velocity
    this.response = response
    this.damping = damping
    this.onUpdate = onUpdate
    this.onRest = onRest
    this.frame = null
    this.lastTime = null
  }

  // Hand the release velocity in and the animation continues at exactly the
  // speed the finger was moving, so there is no seam between drag and settle.
  to(target, { velocity } = {}) {
    this.target = target
    if (velocity !== undefined) this.velocity = velocity

    if (prefersReducedMotion()) {
      this.finish()
      return
    }

    if (this.frame === null) {
      this.lastTime = null
      this.frame = requestAnimationFrame((time) => this.tick(time))
    }
  }

  // Jump straight to the target and stop, without an onRest surprise later.
  finish() {
    this.stop()
    this.value = this.target
    this.velocity = 0
    if (this.onUpdate) this.onUpdate(this.value)
    if (this.onRest) this.onRest(this.value)
  }

  stop() {
    if (this.frame !== null) cancelAnimationFrame(this.frame)
    this.frame = null
    this.lastTime = null
  }

  get animating() {
    return this.frame !== null
  }

  tick(time) {
    const elapsed = this.lastTime === null ? MAX_STEP : Math.min((time - this.lastTime) / 1000, 0.064)
    this.lastTime = time

    const omega = (2 * Math.PI) / this.response
    let remaining = elapsed

    while (remaining > 0) {
      const step = Math.min(remaining, MAX_STEP)
      const acceleration = (omega * omega * (this.target - this.value)) - (2 * this.damping * omega * this.velocity)
      this.velocity += acceleration * step
      this.value += this.velocity * step
      remaining -= step
    }

    const settled = Math.abs(this.target - this.value) < REST_VALUE && Math.abs(this.velocity) < REST_VELOCITY

    if (settled) {
      this.finish()
      return
    }

    if (this.onUpdate) this.onUpdate(this.value)
    this.frame = requestAnimationFrame((next) => this.tick(next))
  }
}

// Progressive resistance past a boundary: the further out you drag, the less
// the thing follows. A hard stop reads as frozen; this reads as "nothing more
// here". `overshoot` and `dimension` share whatever unit the caller uses.
export function rubberband(overshoot, dimension, constant = 0.55) {
  if (dimension <= 0) return overshoot
  return (overshoot * dimension * constant) / (dimension + (constant * Math.abs(overshoot)))
}
